import { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { auth } from '../config/firebase';
import { onAuthChange } from '../services/auth_service';
import { isAdminUser, isSuperUser, isConsultantUser } from '../services/permission_service';
import { handlePermissionError } from '../utils/error_handling';
import { logError, logInfo, logSystemEvent } from '../utils/logs';

const AuthContext = createContext();

export { AuthContext };

const shouldSkipBackendValidation = () => {
	if (typeof globalThis === 'undefined') {
		return false;
	}

	const inIframe = globalThis.self !== globalThis.top;
	const currentPath = globalThis.location.pathname;
	const isLookerEmbedPath = currentPath === '/looker-wrapper' || currentPath === '/looker';

	return inIframe && isLookerEmbedPath;
};

const BACKEND_VALIDATION_MAX_RETRIES = 2;
const FAST_BACKEND_VALIDATION_RETRY_DELAY_MS = 400;
const SLOW_BACKEND_VALIDATION_RETRY_DELAY_MS = 1200;
const BACKEND_VALIDATION_TRANSIENT_RECOVERY_DELAY_MS = 1500;
const BACKEND_VALIDATION_MAX_TRANSIENT_RECOVERY_ATTEMPTS = 1;
const AUTH_CLEAR_DEBOUNCE_MS = 2200;
const AUTH_CLEAR_RECOVERY_MAX_ATTEMPTS = 3;
const AUTH_CLEAR_RECOVERY_RETRY_DELAY_MS = 700;
const HTTP_TOO_MANY_REQUESTS_STATUS = 429;
const HTTP_SERVER_ERROR_MIN_STATUS = 500;
const HTTP_SERVER_ERROR_MAX_STATUS = 599;
const BACKEND_VALIDATION_RETRY_DELAYS_MS = [
	FAST_BACKEND_VALIDATION_RETRY_DELAY_MS,
	SLOW_BACKEND_VALIDATION_RETRY_DELAY_MS,
];
const DEFAULT_BACKEND_VALIDATION_RETRY_DELAY_MS = SLOW_BACKEND_VALIDATION_RETRY_DELAY_MS;

const TRANSIENT_ERROR_MESSAGE_PATTERNS = [
	'network',
	'timeout',
	'failed to fetch',
	'request timed out',
	'too many requests',
	'rate limit',
	'http 429',
	'service unavailable',
	'bad gateway',
	'gateway timeout',
	'http 50',
];

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const isTransientValidationError = error => {
	if (error && typeof error === 'object') {
		const status = Number(error.status);
		if (
			status === HTTP_TOO_MANY_REQUESTS_STATUS ||
			(status >= HTTP_SERVER_ERROR_MIN_STATUS && status <= HTTP_SERVER_ERROR_MAX_STATUS)
		) {
			return true;
		}
	}

	const message = (error instanceof Error ? error.message : String(error || ''))
		.toLowerCase()
		.trim();

	if (!message) {
		return false;
	}

	return TRANSIENT_ERROR_MESSAGE_PATTERNS.some(pattern => message.includes(pattern));
};

const getValidationRetryDelay = attempt =>
	BACKEND_VALIDATION_RETRY_DELAYS_MS[attempt] || DEFAULT_BACKEND_VALIDATION_RETRY_DELAY_MS;

const shouldRetryValidation = (error, attempt) =>
	isTransientValidationError(error) && attempt < BACKEND_VALIDATION_MAX_RETRIES;

const scheduleValidationRetry = async (firebaseUser, error, attempt) => {
	if (!shouldRetryValidation(error, attempt)) {
		return false;
	}

	const retryDelay = getValidationRetryDelay(attempt);
	logSystemEvent('backend_auth_validation_retry_scheduled', 'warning', {
		uid: firebaseUser.uid,
		attempt: attempt + 1,
		retryDelayMs: retryDelay,
		error: error instanceof Error ? error.message : String(error),
	});

	await sleep(retryDelay);
	return true;
};

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [isLoadingAuth, setIsLoadingAuth] = useState(true);
	const [isAuthRecoveryPending, setIsAuthRecoveryPending] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [backendValidated, setBackendValidated] = useState(false);
	const [backendAuthStage, setBackendAuthStage] = useState('unknown');
	const [authErrorMessage, setAuthErrorMessage] = useState('');
	const [backendUser, setBackendUser] = useState(null);
	const backendValidatedRef = useRef(false);
	const isAuthenticatedRef = useRef(false);
	const latestAuthChangeIdRef = useRef(0);
	const latestValidationRunIdRef = useRef(0);
	const activeFirebaseUidRef = useRef(null);
	const transientRecoveryAttemptsRef = useRef(0);
	const transientRecoveryTimerRef = useRef(null);
	const pendingAuthClearTimerRef = useRef(null);
	const hadStableSessionRef = useRef(false);

	useEffect(() => {
		backendValidatedRef.current = backendValidated;
	}, [backendValidated]);

	useEffect(() => {
		isAuthenticatedRef.current = isAuthenticated;
	}, [isAuthenticated]);

	const applyBackendValidationResult = (firebaseUser, backendResponse) => {
		const authStage = backendResponse.authStage;
		const validated =
			authStage === 'validated' ||
			((authStage === null || authStage === undefined) &&
				backendResponse.requiresTOTP !== true &&
				backendResponse.requiresTOTPSetup !== true);

		setBackendUser(backendResponse.user || null);
		setBackendValidated(validated);
		setBackendAuthStage(backendResponse.authStage || (validated ? 'validated' : 'unknown'));
		setAuthErrorMessage('');

		logSystemEvent('backend_auth_validation_completed', 'info', {
			uid: firebaseUser.uid,
			authStage: backendResponse.authStage ?? null,
			backendValidated: validated,
		});

		transientRecoveryAttemptsRef.current = 0;
		if (transientRecoveryTimerRef.current) {
			clearTimeout(transientRecoveryTimerRef.current);
			transientRecoveryTimerRef.current = null;
		}
	};

	const handleBackendValidationError = (firebaseUser, error, options = {}) => {
		const preserveExistingValidation = options.preserveExistingValidation === true;

		logError('Backend validation failed on auth restore', error);
		logSystemEvent('backend_auth_validation_failed', 'warning', {
			uid: firebaseUser.uid,
			error: error instanceof Error ? error.message : String(error),
			preservedValidation: preserveExistingValidation,
		});

		if (preserveExistingValidation) {
			setBackendAuthStage('validated');
			setAuthErrorMessage('');
			return;
		}

		setBackendValidated(false);
		setBackendAuthStage('validation_error');
		setAuthErrorMessage(
			error instanceof Error ? error.message : 'Backend authentication validation failed.'
		);
	};

	useEffect(() => {
		let isActive = true;

		const isStaleValidationRun = (validationRunId, firebaseUid) => {
			return (
				!isActive ||
				validationRunId !== latestValidationRunIdRef.current ||
				activeFirebaseUidRef.current !== firebaseUid
			);
		};

		const clearAuthState = () => {
			activeFirebaseUidRef.current = null;
			transientRecoveryAttemptsRef.current = 0;
			hadStableSessionRef.current = false;
			if (transientRecoveryTimerRef.current) {
				clearTimeout(transientRecoveryTimerRef.current);
				transientRecoveryTimerRef.current = null;
			}
			setUser(null);
			setIsAuthenticated(false);
			setIsAuthRecoveryPending(false);
			setBackendValidated(false);
			setBackendAuthStage('signed_out');
			setBackendUser(null);
			setIsLoadingAuth(false);
		};

		const recoverFirebaseUserAfterClearSignal = async ({ authChangeId }) => {
			for (let attempt = 1; attempt <= AUTH_CLEAR_RECOVERY_MAX_ATTEMPTS; attempt += 1) {
				if (!isActive || latestAuthChangeIdRef.current !== authChangeId) {
					return null;
				}

				const recoveredFirebaseUser = auth.currentUser;
				if (recoveredFirebaseUser?.uid) {
					logSystemEvent('firebase_user_clear_recovered_with_retry', 'warning', {
						uid: recoveredFirebaseUser.uid,
						authChangeId,
						attempt,
						path: globalThis?.location?.pathname || null,
					});
					return recoveredFirebaseUser;
				}

				if (attempt < AUTH_CLEAR_RECOVERY_MAX_ATTEMPTS) {
					await sleep(AUTH_CLEAR_RECOVERY_RETRY_DELAY_MS);
				}
			}

			return null;
		};

		const finalizeRecoveredUser = recoveredFirebaseUser => {
			hadStableSessionRef.current = true;
			activeFirebaseUidRef.current = recoveredFirebaseUser.uid;
			setUser(recoveredFirebaseUser);
			setIsAuthenticated(true);
			setIsAuthRecoveryPending(false);

			if (shouldSkipBackendValidation()) {
				setBackendValidated(true);
				setBackendAuthStage('validated');
				setIsLoadingAuth(false);
				pendingAuthClearTimerRef.current = null;
				return;
			}

			latestValidationRunIdRef.current += 1;
			const recoveryValidationRunId = latestValidationRunIdRef.current;
			void validateBackendWithRetry(recoveredFirebaseUser, recoveryValidationRunId);
			pendingAuthClearTimerRef.current = null;
		};

		const recoverOrClearAuthState = async authChangeId => {
			if (!isActive || latestAuthChangeIdRef.current !== authChangeId) {
				pendingAuthClearTimerRef.current = null;
				return;
			}

			const immediateRecoveredFirebaseUser = auth.currentUser;
			if (immediateRecoveredFirebaseUser?.uid) {
				logSystemEvent('firebase_user_clear_recovered_with_current_user', 'warning', {
					uid: immediateRecoveredFirebaseUser.uid,
					authChangeId,
					path: globalThis?.location?.pathname || null,
				});
				finalizeRecoveredUser(immediateRecoveredFirebaseUser);
				return;
			}

			const recoveredFirebaseUser = await recoverFirebaseUserAfterClearSignal({
				authChangeId,
			});

			if (!isActive || latestAuthChangeIdRef.current !== authChangeId) {
				pendingAuthClearTimerRef.current = null;
				return;
			}

			if (recoveredFirebaseUser?.uid) {
				finalizeRecoveredUser(recoveredFirebaseUser);
				return;
			}

			clearAuthState();
			pendingAuthClearTimerRef.current = null;
		};

		const validateBackendWithRetry = async (firebaseUser, validationRunId) => {
			const { loginUser } = await import('../services/api_service');
			let lastError = null;
			const firebaseUid = firebaseUser.uid;

			for (let attempt = 0; attempt <= BACKEND_VALIDATION_MAX_RETRIES; attempt += 1) {
				try {
					logInfo('Backend auth validation started on auth restore', {
						uid: firebaseUser.uid,
						attempt: attempt + 1,
					});

					const backendResponse = await loginUser();
					if (isStaleValidationRun(validationRunId, firebaseUid)) {
						return;
					}

					applyBackendValidationResult(firebaseUser, backendResponse);
					return;
				} catch (error) {
					lastError = error;

					const shouldContinue = await scheduleValidationRetry(
						firebaseUser,
						error,
						attempt
					);

					if (!shouldContinue) {
						break;
					}
					if (isStaleValidationRun(validationRunId, firebaseUid)) {
						return;
					}
				}
			}

			if (isStaleValidationRun(validationRunId, firebaseUid)) {
				return;
			}

			if (isTransientValidationError(lastError)) {
				const canScheduleRecovery =
					transientRecoveryAttemptsRef.current <
					BACKEND_VALIDATION_MAX_TRANSIENT_RECOVERY_ATTEMPTS;

				if (canScheduleRecovery) {
					transientRecoveryAttemptsRef.current += 1;
					setBackendAuthStage('unknown');
					setAuthErrorMessage('');
					logSystemEvent(
						'backend_auth_validation_transient_recovery_scheduled',
						'warning',
						{
							uid: firebaseUser.uid,
							recoveryAttempt: transientRecoveryAttemptsRef.current,
							delayMs: BACKEND_VALIDATION_TRANSIENT_RECOVERY_DELAY_MS,
							error:
								lastError instanceof Error ? lastError.message : String(lastError),
						}
					);

					if (transientRecoveryTimerRef.current) {
						clearTimeout(transientRecoveryTimerRef.current);
					}

					transientRecoveryTimerRef.current = setTimeout(() => {
						if (isStaleValidationRun(validationRunId, firebaseUid)) {
							return;
						}
						void validateBackendWithRetry(firebaseUser, validationRunId);
					}, BACKEND_VALIDATION_TRANSIENT_RECOVERY_DELAY_MS);
					return;
				}
			}

			const preserveExistingValidation =
				backendValidatedRef.current && isTransientValidationError(lastError);
			handleBackendValidationError(firebaseUser, lastError, {
				preserveExistingValidation,
			});
		};

		const unsubscribe = onAuthChange(async firebaseUser => {
			latestAuthChangeIdRef.current += 1;
			const authChangeId = latestAuthChangeIdRef.current;

			logSystemEvent('auth_state_changed', 'info', {
				hasFirebaseUser: !!firebaseUser,
				uid: firebaseUser?.uid || null,
				path: globalThis?.location?.pathname || null,
				authChangeId,
			});

			if (firebaseUser) {
				hadStableSessionRef.current = true;
				if (pendingAuthClearTimerRef.current) {
					clearTimeout(pendingAuthClearTimerRef.current);
					pendingAuthClearTimerRef.current = null;
				}
				activeFirebaseUidRef.current = firebaseUser.uid;
				setIsLoadingAuth(true);
				setUser(firebaseUser);
				setIsAuthenticated(true);
				setIsAuthRecoveryPending(false);

				if (shouldSkipBackendValidation()) {
					logSystemEvent('backend_auth_validation_skipped_for_looker_iframe', 'info', {
						uid: firebaseUser.uid,
						path: globalThis?.location?.pathname || null,
					});
					setBackendValidated(true);
					setBackendAuthStage('validated');
					setIsLoadingAuth(false);
					return;
				}

				latestValidationRunIdRef.current += 1;
				const validationRunId = latestValidationRunIdRef.current;
				await validateBackendWithRetry(firebaseUser, validationRunId);
			} else {
				if (pendingAuthClearTimerRef.current) {
					clearTimeout(pendingAuthClearTimerRef.current);
					pendingAuthClearTimerRef.current = null;
				}

				const shouldDebounceClear =
					hadStableSessionRef.current || isAuthenticatedRef.current;
				logSystemEvent('firebase_user_cleared', 'warning', {
					path: globalThis?.location?.pathname || null,
					inIframe:
						typeof globalThis !== 'undefined' && globalThis.self !== globalThis.top,
					debounced: shouldDebounceClear,
					authChangeId,
				});

				if (shouldDebounceClear) {
					// Invalidate in-flight backend validations from the prior session.
					activeFirebaseUidRef.current = null;
					latestValidationRunIdRef.current += 1;

					// Reflect signed-out UI state immediately while allowing brief recovery retries.
					setUser(null);
					setIsAuthenticated(false);
					setIsAuthRecoveryPending(true);
					setBackendValidated(false);
					setBackendAuthStage('signed_out');
					setBackendUser(null);
					setIsLoadingAuth(true);
					pendingAuthClearTimerRef.current = setTimeout(() => {
						void recoverOrClearAuthState(authChangeId);
					}, AUTH_CLEAR_DEBOUNCE_MS);
					return;
				}

				clearAuthState();
				return;
			}
			if (isActive && latestAuthChangeIdRef.current === authChangeId) {
				setIsLoadingAuth(false);
			}
		});

		return () => {
			isActive = false;
			if (pendingAuthClearTimerRef.current) {
				clearTimeout(pendingAuthClearTimerRef.current);
				pendingAuthClearTimerRef.current = null;
			}
			if (transientRecoveryTimerRef.current) {
				clearTimeout(transientRecoveryTimerRef.current);
				transientRecoveryTimerRef.current = null;
			}
			unsubscribe();
		};
	}, []);

	const contextValue = useMemo(
		() => ({
			user,
			isAuthenticated,
			isLoadingAuth,
			isAuthRecoveryPending,
			backendValidated,
			backendAuthStage,
			setBackendValidated,
			authErrorMessage,
			setAuthErrorMessage,
			backendUser,
			setBackendUser,
			twoFactorState: {
				enabled:
					backendUser?.twoFactorEnabled === true ||
					backendUser?.two_factor_enabled === true,
				method: backendUser?.twoFactorMethod ?? backendUser?.two_factor_method ?? null,
			},
		}),
		[
			user,
			isAuthenticated,
			isLoadingAuth,
			isAuthRecoveryPending,
			backendValidated,
			backendAuthStage,
			authErrorMessage,
			backendUser,
		]
	);

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useAuth = () => {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
};

export const useRequireAuth = () => {
	const { user, isAuthenticated, isLoadingAuth, authErrorMessage } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		if (!isLoadingAuth && !isAuthenticated) {
			navigate('/login');
		}
	}, [isAuthenticated, isLoadingAuth, navigate]);

	return { user, isAuthenticated, isLoadingAuth, authErrorMessage };
};

export const useAdminAccess = () => {
	const { isAuthenticated, isLoadingAuth, backendValidated } = useAuth();
	const [hasAdminAccess, setHasAdminAccess] = useState(null);
	const [isLoadingAccess, setIsLoadingAccess] = useState(true);
	const isTest = process.env.NODE_ENV === 'test';

	useEffect(() => {
		const checkAdminAccess = async () => {
			if (!isAuthenticated || (!isTest && (isLoadingAuth || !backendValidated))) {
				setHasAdminAccess(null);
				setIsLoadingAccess(false);
				return;
			}

			setIsLoadingAccess(true);
			try {
				// The Admin section is superuser only: user and role management,
				// organisations, Looker configuration, integration keys and logs.
				// Admins keep everything outside it, including reading the day rate
				// and changing commercial terms on the Relationship tab.
				const isSuper = await isSuperUser();
				setHasAdminAccess(isSuper);
			} catch (error) {
				handlePermissionError(error, { context: 'useAdminAccess' });
				setHasAdminAccess(false);
			} finally {
				setIsLoadingAccess(false);
			}
		};

		checkAdminAccess();
	}, [isAuthenticated, isLoadingAuth, backendValidated, isTest]);

	return { hasAdminAccess, isLoadingAccess };
};

export const useUserRole = () => {
	const { isAuthenticated, isLoadingAuth, backendValidated } = useAuth();
	const [userRole, setUserRole] = useState(null);
	const [isLoadingRole, setIsLoadingRole] = useState(true);
	const isTest = process.env.NODE_ENV === 'test';

	useEffect(() => {
		const checkUserRole = async () => {
			if (!isAuthenticated || (!isTest && (isLoadingAuth || !backendValidated))) {
				setUserRole(null);
				setIsLoadingRole(false);
				return;
			}

			try {
				const isAdmin = await isAdminUser();
				const isSuper = await isSuperUser();
				const isConsultant = await isConsultantUser();

				if (isSuper) {
					setUserRole('superuser');
				} else if (isAdmin) {
					setUserRole('admin');
				} else if (isConsultant) {
					setUserRole('consultant');
				} else {
					setUserRole('client');
				}
			} catch (error) {
				handlePermissionError(error, { context: 'useUserRole' });
				setUserRole(null);
			} finally {
				setIsLoadingRole(false);
			}
		};

		checkUserRole();
	}, [isAuthenticated, isLoadingAuth, backendValidated, isTest]);

	return { userRole, isLoadingRole };
};
